import React, { useCallback, useEffect, useState } from 'react';
import { useTheme } from '../../../contexts/ThemeContext';
import { Button, ConfirmationDialog } from '../../../components/ui';
import { TrashIcon } from '../../../components/ui/Icons';
import { cn } from '../../../lib/utils';
import { API_BASE_URL } from '../../../config/apiConfig';
import { useEntities } from '../../../hooks/useSWR';
import { mutate as mutateSWR } from 'swr';

const getAuthHeaders = () => {
  const token = localStorage.getItem('authToken');
  const savedUser = localStorage.getItem('user');
  let userId = null;
  if (savedUser) {
    try {
      userId = JSON.parse(savedUser).id?.toString();
    } catch {
      userId = null;
    }
  }
  return {
    'Content-Type': 'application/json',
    ...(userId && { 'user-id': userId }),
    ...(token && { Authorization: `Bearer ${token}` }),
  };
};

const mapAssignmentRow = (a) => ({
  id: a.id,
  userId: a.userId,
  userLabel:
    a.user?.userDisplayName || a.user?.userEmail || `User #${a.userId}`,
});

const ManualAssignmentsTab = ({ selectedRecord, onAssignmentsSaved }) => {
  const { theme } = useTheme();
  const manualId = selectedRecord?.id;
  const { data: users = [] } = useEntities('/users');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [adding, setAdding] = useState(false);
  const [removing, setRemoving] = useState(false);
  const [error, setError] = useState(null);
  const [newUserId, setNewUserId] = useState('');
  const [removeConfirm, setRemoveConfirm] = useState(null);

  const loadAssignments = useCallback(async () => {
    if (!manualId) return;
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(
        `${API_BASE_URL}/manual-assignment/by-manual/${manualId}`,
        { headers: getAuthHeaders() }
      );
      if (!res.ok) throw new Error('Failed to load assignments');
      const data = await res.json();
      setRows((data || []).map(mapAssignmentRow));
    } catch (e) {
      setError(e.message);
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [manualId]);

  useEffect(() => {
    loadAssignments();
  }, [loadAssignments]);

  const persistAssignments = async (userIds) => {
    const res = await fetch(`${API_BASE_URL}/manual-assignment/bulk`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify({
        manualId,
        assignments: userIds.map((userId) => ({ userId })),
      }),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.message || 'Failed to save assignments');
    }
    await loadAssignments();
    if (manualId) {
      await mutateSWR(`/manual-assignment/by-manual/${manualId}`);
    }
    onAssignmentsSaved?.();
  };

  const addAssignee = async () => {
    const uid = parseInt(newUserId, 10);
    if (!uid || !manualId) return;
    if (rows.some((r) => r.userId === uid)) {
      setError('User is already assigned');
      return;
    }

    setAdding(true);
    setError(null);
    try {
      await persistAssignments([...rows.map((r) => r.userId), uid]);
      setNewUserId('');
    } catch (e) {
      setError(e.message);
    } finally {
      setAdding(false);
    }
  };

  const openRemoveConfirm = (row) => {
    setRemoveConfirm({ id: row.id, userId: row.userId, userLabel: row.userLabel });
  };

  const closeRemoveConfirm = () => {
    if (!removing) setRemoveConfirm(null);
  };

  const confirmRemoveRow = async () => {
    if (!removeConfirm || !manualId) return;

    setRemoving(true);
    setError(null);
    try {
      if (removeConfirm.id) {
        const res = await fetch(
          `${API_BASE_URL}/manual-assignment/${removeConfirm.id}`,
          { method: 'DELETE', headers: getAuthHeaders() }
        );
        if (!res.ok) {
          const err = await res.json().catch(() => ({}));
          throw new Error(err.message || 'Failed to remove assignee');
        }
      } else {
        const nextIds = rows
          .filter((r) => r.userId !== removeConfirm.userId)
          .map((r) => r.userId);
        await persistAssignments(nextIds);
      }
      setRemoveConfirm(null);
      await loadAssignments();
      if (manualId) {
        await mutateSWR(`/manual-assignment/by-manual/${manualId}`);
      }
      onAssignmentsSaved?.();
    } catch (e) {
      setError(e.message);
    } finally {
      setRemoving(false);
    }
  };

  const assignedUserIds = new Set(rows.map((r) => r.userId));
  const busy = adding || removing;

  if (!manualId) {
    return (
      <p className={`text-sm ${theme.text.secondary} p-4`}>
        Save the manual first, then assign users.
      </p>
    );
  }

  return (
    <div className="p-4 space-y-4 overflow-y-auto max-h-[calc(100vh-12rem)]">
      <p className={`text-sm ${theme.text.secondary}`}>
        Assign users who can open this manual under <strong>My Manuals</strong>. Only manuals
        with status <strong>Valid</strong> can be acknowledged.
      </p>

      {error && (
        <div className="text-sm text-red-600 bg-red-50 dark:bg-red-900/20 p-2 rounded">
          {error}
        </div>
      )}

      <div className="flex flex-wrap gap-2 items-end">
        <div className="flex-1 min-w-[200px]">
          <label className={`block text-xs mb-1 ${theme.text.secondary}`}>User</label>
          <select
            className={`w-full rounded border px-2 py-1.5 text-sm ${theme.border.light} ${theme.colors.surface}`}
            value={newUserId}
            onChange={(e) => setNewUserId(e.target.value)}
            disabled={busy}
          >
            <option value="">Select user…</option>
            {users
              .filter((u) => u.userIsActive !== false && !assignedUserIds.has(u.id))
              .map((u) => (
                <option key={u.id} value={u.id}>
                  {u.userDisplayName || u.userEmail} ({u.userEmail})
                </option>
              ))}
          </select>
        </div>
        <Button type="button" variant="secondary" onClick={addAssignee} disabled={!newUserId || busy}>
          {adding ? 'Adding…' : 'Add user'}
        </Button>
      </div>

      {loading ? (
        <p className={`text-sm ${theme.text.secondary}`}>Loading…</p>
      ) : (
        <table className={`w-full text-sm border ${theme.border.light}`}>
          <thead>
            <tr className={theme.colors.background}>
              <th className="text-left p-2">User</th>
              <th className="p-2 w-12" />
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr>
                <td colSpan={2} className={`p-4 text-center ${theme.text.secondary}`}>
                  No users assigned yet.
                </td>
              </tr>
            ) : (
              rows.map((r) => (
                <tr key={r.userId} className={`border-t ${theme.border.light}`}>
                  <td className="p-2">{r.userLabel}</td>
                  <td className="p-2">
                    <button
                      type="button"
                      onClick={() => openRemoveConfirm(r)}
                      disabled={busy}
                      className={cn(
                        theme.colors.danger,
                        theme.text.inverse,
                        'hover:scale-105 rounded p-1',
                        busy && 'opacity-50 cursor-not-allowed'
                      )}
                      title="Remove user"
                      aria-label={`Remove ${r.userLabel}`}
                    >
                      <TrashIcon className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      )}

      <ConfirmationDialog
        isOpen={!!removeConfirm}
        onClose={closeRemoveConfirm}
        onConfirm={confirmRemoveRow}
        title="Remove assignee"
        message={
          removeConfirm
            ? `Remove ${removeConfirm.userLabel} from this manual?`
            : 'Remove this assignee?'
        }
        warningMessage="The user will no longer see this manual in My Manuals."
        confirmText="Remove"
        cancelText="Cancel"
        confirmVariant="destructive"
        isLoading={removing}
      />
    </div>
  );
};

export default ManualAssignmentsTab;
